import React, { useEffect } from 'react';
import { View, Text } from 'react-native';
import Svg, { Circle, Defs, LinearGradient as SvgLinearGradient, Stop } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { labelThemeColor, capitalizeLabel } from '@/lib/scoring/score-display';
import type { VentureLabel } from '@/lib/scoring/methodology';

export type ScoreRingSize = 'hero' | 'md' | 'sm';

interface ScoreRingProps {
  score?: number;
  ventureLabel?: VentureLabel | string;
  size?: ScoreRingSize | number;
  showLabelText?: boolean;
}

const SIZE_MAP: Record<ScoreRingSize, number> = {
  hero: 140,
  md: 88,
  sm: 56,
};

const STROKE_MAP: Record<ScoreRingSize, number> = {
  hero: 12,
  md: 8,
  sm: 6,
};

const NUMBER_FONT_MAP: Record<ScoreRingSize, number> = {
  hero: 44,
  md: 24,
  sm: 16,
};

const LABEL_FONT_MAP: Record<ScoreRingSize, number> = {
  hero: 11,
  md: 9,
  sm: 8,
};

function resolveSize(size: ScoreRingSize | number): { px: number; stroke: number; numberFont: number; labelFont: number } {
  if (typeof size === 'number') {
    const ratio = size / SIZE_MAP.hero;
    return {
      px: size,
      stroke: Math.max(4, Math.round(STROKE_MAP.hero * ratio)),
      numberFont: Math.max(12, Math.round(NUMBER_FONT_MAP.hero * ratio)),
      labelFont: Math.max(7, Math.round(LABEL_FONT_MAP.hero * ratio)),
    };
  }
  return {
    px: SIZE_MAP[size],
    stroke: STROKE_MAP[size],
    numberFont: NUMBER_FONT_MAP[size],
    labelFont: LABEL_FONT_MAP[size],
  };
}

export function ScoreRing({
  score,
  ventureLabel,
  size = 'md',
  showLabelText = true,
}: ScoreRingProps) {
  const colors = useThemeColors();
  const { px, stroke, numberFont, labelFont } = resolveSize(size);
  const radius = (px - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const isEmpty = score === undefined;

  const progress = useSharedValue(0);

  useEffect(() => {
    if (isEmpty) {
      progress.value = 0;
      return;
    }
    progress.value = withDelay(
      150,
      withTiming(score / 100, {
        duration: 1200,
        easing: Easing.out(Easing.cubic),
      })
    );
  }, [score, isEmpty]);

  const fadeIn = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 0.05], [0.4, 1]),
  }));

  const arcColor = !isEmpty && ventureLabel
    ? labelThemeColor(ventureLabel as string, colors)
    : colors.primary[500];

  const strokeDashoffset = isEmpty ? circumference : circumference * (1 - score! / 100);

  return (
    <Animated.View style={[{ width: px, height: px }, !isEmpty && fadeIn]}>
      <Svg width={px} height={px} viewBox={`0 0 ${px} ${px}`}>
        {/* Background track */}
        <Circle
          cx={px / 2}
          cy={px / 2}
          r={radius}
          stroke={colors.border.light}
          strokeWidth={stroke}
          fill="none"
          {...(isEmpty ? { strokeDasharray: '6 8' } : {})}
        />
        {/* Progress arc */}
        {!isEmpty && (
          <Circle
            cx={px / 2}
            cy={px / 2}
            r={radius}
            stroke={arcColor}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            fill="none"
            transform={`rotate(-90 ${px / 2} ${px / 2})`}
          />
        )}
      </Svg>
      <View
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isEmpty ? (
          <>
            <Text style={{ color: colors.text.tertiary, fontSize: numberFont, fontWeight: '300' }}>
              —
            </Text>
            {showLabelText && (
              <Text
                style={{
                  color: colors.text.tertiary,
                  fontSize: labelFont,
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginTop: 2,
                }}
                numberOfLines={1}
              >
                No Score
              </Text>
            )}
          </>
        ) : (
          <>
            <Text
              style={{
                color: colors.text.primary,
                fontSize: numberFont,
                fontWeight: '700',
                letterSpacing: -1,
              }}
            >
              {score}
            </Text>
            {showLabelText && ventureLabel && (
              <Text
                style={{
                  color: arcColor,
                  fontSize: labelFont,
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginTop: 2,
                }}
                numberOfLines={1}
              >
                {capitalizeLabel(ventureLabel as string)}
              </Text>
            )}
          </>
        )}
      </View>
    </Animated.View>
  );
}
